<template>
    <container>
        <hox-loading-layer v-if="isLoading" size="large" />
        <div v-else class="clients">
            <div class="clients__action-items">
                <div class="clients__tabs">
                    <SectionNav />
                    <span>My Clients</span>
                </div>
                <SortClients v-model="sortClientsBy" class="clients__action-item" />
                <Button
                    v-if="userCanAddClient"
                    class="clients__action-item"
                    size="large"
                    type="primary"
                    data-testid="add-client_button"
                    @click="editClient"
                >
                    Add New Client
                </Button>
            </div>
            <div>
                <Row :gutter="15">
                    <Col v-for="client of clients" :key="client._id" :xs="24" :sm="12" :md="8" :lg="6">
                        <ClientCard
                            :id="client._id"
                            :logo="client.logoUrl"
                            :name="client.name"
                            @edit="editClient(client)"
                            @manageTeam="showTeam(client._id)"
                        />
                    </Col>
                </Row>
            </div>
            <ClientModal
                :is-modal-visible="isClientFormVisible"
                :client="editedClient"
                :sort-clients-by="sortClientsBy"
                @close="toggleClientModal"
            />

            <TeamModal
                v-if="teamModalIsVisible"
                :client-id="teamClientId"
                entity-type="client"
                @close="toggleTeamModalIsVisible"
            />
        </div>
    </container>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import bus from "@/bus";

import ClientCard from "@/components/Client/ClientCard.vue";
import SortClients from "@/components/Client/SortClients.vue";
import Container from "@/components/Container.vue";
import SectionNav from "@/components/SectionNav.vue";
import TeamModal from "@/components/TeamModal.vue";
import ClientModal from "@/components/Client/ClientModal.vue";

import clientsQuery from "@/apollo/queries/Clients.gql";
import type { Client, Err } from "@/types/types";
import { uploadConfigExpired } from "@/utils";
import { defaultClientSortBy } from "@/enums/sort";
import { AppRouteNames } from "@/router/constants";
import GA4 from "@/enums/ga4Events";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { hasScope, userCan } from "@/services/Auth";
import { AuthGetters } from "@/store/modules/auth";

export default defineComponent({
    // eslint-disable-next-line vue/multi-word-component-names
    name: "Clients",
    components: {
        SortClients,
        ClientCard,
        Container,
        TeamModal,
        SectionNav,
        ClientModal
    },

    emits: [],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isClientFormVisible: false,
            isLoading: true,
            sortClientsBy: defaultClientSortBy,
            teamClientId: "",
            teamModalIsVisible: false,
            editedClient: null as Client | null,
            clients: [] as Client[]
        };
    },

    computed: {
        userScope() {
            return this.$store.state.auth.me?.scope;
        },

        userCanAddClient() {
            return userCan(USER_ACTIONS.canAddClient);
        }
    },

    watch: {
        userScope: {
            handler() {
                if (this.clients.some(client => !hasScope({ clientId: client._id }, false))) {
                    void this.$apollo.queries.clients?.refetch();
                }
            }
        }
    },

    created() {
        void this.$apollo.queries.clients?.refetch();
    },

    methods: {
        async editClient(client: Client | null) {
            if (client && uploadConfigExpired(client.mastheadUploadConfig)) {
                // If it isn't then refetch the clients query and update the passed in
                //  client with the data from the new request
                await this.$apollo.queries.clients?.refetch();
                const updatedClient = this.clients.find(c => c._id === client._id);
                if (updatedClient) {
                    Object.assign(client, updatedClient);
                }
            }

            this.editedClient = client;

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                featureContext: client ? GA4.FEATURE_CONTEXTS.EDIT_CLIENT : GA4.FEATURE_CONTEXTS.ADD_CLIENT
            });

            this.toggleClientModal();
        },

        showTeam(clientId: string) {
            this.teamClientId = clientId;
            this.toggleTeamModalIsVisible();
        },

        toggleClientModal() {
            this.isClientFormVisible = !this.isClientFormVisible;
        },

        toggleTeamModalIsVisible() {
            this.teamModalIsVisible = !this.teamModalIsVisible;
        }
    },

    apollo: {
        clients: {
            query: clientsQuery,
            variables() {
                return {
                    orderBy: this.sortClientsBy,
                    wppProjectId: this.$store.getters[AuthGetters.effectiveWppProjectId] as string | undefined
                };
            },
            error(e) {
                bus.$emit("apolloErrorEvent", e as unknown as Err);
            },
            result({ data }: { data: { clients: Client[] } }) {
                if (typeof data.clients === "undefined") {
                    void this.$router.push({ name: "UserNotFound" });
                    return;
                }

                if (data.clients.length === 1) {
                    // if user can't do anything in the client screen lets redirect it to the campaign dashboard
                    if (
                        data.clients[0]?._id &&
                        !userCan(USER_ACTIONS.canAddClient) &&
                        !hasScope({ clientId: data.clients[0]._id })
                    ) {
                        void this.$router.replace({
                            name: AppRouteNames.CLIENT_CAMPAIGNS,
                            params: { clientId: data.clients[0]._id }
                        });
                    }
                }

                this.isLoading = false;
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.clients__action-items {
    display: flex;
    justify-content: flex-end;
    padding: 15px 0;
}

.clients__action-item {
    flex: 0 0;
    display: flex;
    align-items: center;
    margin-left: 20px;
}
</style>
